import { motion } from 'motion/react';
import { TryPrompt } from './TryPrompt';
import { FIGURES } from './figures';
import { ease } from '../../lib/motion';
import { md } from '../../lib/md';
import { useLocale } from '../../state/useLocale';
import type { TutorialCard } from '../../types';

/**
 * One card: one tool.
 *
 * The prose column says what the tool is and why it exists; the column beside
 * it shows the mechanism and offers the rep. They are one column each on a
 * narrow screen, prose first, because the sentence is what the diagram and
 * the prompt are both illustrations of.
 *
 * The card slides in by eight pixels and out by eight. Anything further and
 * the reader's eye has to travel back to the start of the line, which is a
 * cost paid on every step of a five-step sequence.
 */

export interface WarmupCardProps {
  card: TutorialCard;
  /** The mission's own working directory; the card's `try.cwd` overrides it. */
  cwd: string;
  /** True when this card's rep has already been passed in this session. */
  passed: boolean;
  onPass(): void;
}

export function WarmupCard({ card, cwd, passed, onPass }: WarmupCardProps) {
  // The overlay swaps cards with `mode="wait"`, so the mounted card is always
  // the card on screen and its figure is always the one that should animate.
  const Figure = card.figure ? FIGURES[card.figure] : null;
  const aside = Figure !== null || card.try !== undefined;

  // The deck advances the way the page reads: a new card arrives from the
  // side the next one is on, which is the right in English and the left in
  // Arabic. Eight pixels either way (docs/i18n.md §7).
  const from = useLocale().dir === 'rtl' ? -8 : 8;

  return (
    <motion.article
      className={aside ? 'warmup-card has-aside' : 'warmup-card'}
      initial={{ opacity: 0, x: from }}
      animate={{ opacity: 1, x: 0, transition: ease }}
      exit={{ opacity: 0, x: -from, transition: ease }}
    >
      <div className="warmup-read">
        {/* `label` when a translation gave the card one, `tool` otherwise.
            Most cards are headed by the command itself, which is not
            translatable; a few are headed by a name from the interface, and
            those carry a `label` (engine: `TutorialCard.label`). `tool` stays
            the anchor a translation is matched by, so it is never overwritten. */}
        <h3 className="warmup-tool">
          <bdi dir={card.label ? 'auto' : 'ltr'}>{card.label ?? card.tool}</bdi>
        </h3>
        <p className="warmup-says">{card.title}</p>
        {card.body && <div className="warmup-body prose" dangerouslySetInnerHTML={md(card.body)} />}

        {card.keys && card.keys.length > 0 && (
          <dl className="warmup-keys">
            {card.keys.map((k) => (
              <div key={k.key}>
                <dt><bdi dir="ltr">{k.key}</bdi></dt>
                <dd>{k.title}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {aside && (
        <div className="warmup-aside">
          {/* An empty figure box is worse than none, and the figures land in
              their own file on their own schedule, so the wrapper collapses
              when whatever it holds draws nothing (`:empty` in warmup.css). */}
          {Figure && (
            <div className="warmup-figure">
              <Figure active />
            </div>
          )}
          {card.try && <TryPrompt rep={card.try} cwd={card.try.cwd ?? cwd} passed={passed} onPass={onPass} />}
        </div>
      )}
    </motion.article>
  );
}

export default WarmupCard;
